import { Typography, SvgIcon, CircularProgress } from '@mui/material'
import React, { Fragment, ChangeEvent, memo, useCallback, useId, useMemo, useState } from 'react'
import tw, { css } from 'twin.macro'
import { LandingPageMajor, RiskMinor } from '@shopify/polaris-icons'
import { showToast } from 'src/utils/toast'

interface Props {
  acceptTypes: Array<string>
  isLoading?: boolean
  fileSizeLimit?: number
  onSelect: (file: File) => void
  inputDataTestId?: string
  /**
   * The line under the prompt describing what may be uploaded. Defaults to the original
   * PNG/JPG/PDF wording so existing callers render exactly as before; pass it when `acceptTypes`
   * is narrower, so the caption cannot contradict what the input actually accepts.
   */
  caption?: string
}

// TODO: make default file input from this component instead of CSV input
const DEFAULT_CAPTION = 'PNG, JPG or PDF (max. 10MB)'

const FileUploadInputComponent = ({
  acceptTypes,
  isLoading,
  fileSizeLimit,
  onSelect,
  inputDataTestId,
  caption = DEFAULT_CAPTION,
}: Props) => {
  /**
   * Unique per instance. The id was previously the literal 'banner-file', which meant two
   * pickers on one page emitted duplicate ids and the second label bound to the FIRST input —
   * clicking one picker opened the other's file dialog. `useId` keeps each label/input pair
   * associated with itself.
   */
  const inputId = useId()
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const onInputImage = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0]

        setError(null)

        if (fileSizeLimit && file.size > fileSizeLimit) {
          e.target.value = ''

          showToast(`File size is bigger then ${fileSizeLimit / 1000000}MB`, {
            variant: 'error',
          })

          setError('Please upload required file.')

          return
        }

        if (!acceptTypes.includes(file.type)) {
          e.target.value = ''

          showToast('File type is not supported', {
            variant: 'error',
          })

          setError('Please upload required file.')

          return
        }

        setError(null)
        onSelect(file)

        e.target.value = ''
      }
    },
    [onSelect],
  )

  const handleDragEnter = useCallback(() => {
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const inputAcceptTypes = useMemo(() => acceptTypes.join(','), [acceptTypes])

  return (
    <Fragment>
      <label htmlFor={inputId}>
        <div
          css={[
            tw`relative border rounded-lg border-dashed px-4 py-8 flex flex-1 flex-col justify-center items-center transition duration-300 ease-linear`,
            styles.container(isDragOver, error),
          ]}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Fragment>
              <SvgIcon css={tw`w-5 h-5`}>
                <LandingPageMajor css={tw`text-shades-1000`} />
              </SvgIcon>
              <Typography css={tw`my-2`} component="span">
                <Typography css={tw`underline text-primary-500`} component="span">
                  Tap to upload
                </Typography>{' '}
                or drag and drop
              </Typography>
              <Typography variant="body2" css={tw`text-shades-400`}>
                {caption}
              </Typography>
              <input
                type="file"
                id={inputId}
                name="file"
                accept={inputAcceptTypes}
                onChange={onInputImage}
                css={styles.input}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                {...(inputDataTestId ? { 'data-testid': inputDataTestId } : {})}
              />
            </Fragment>
          )}
        </div>
      </label>
      {error && (
        <div css={tw`mt-2 flex items-center gap-1`}>
          <RiskMinor width={20} css={tw`fill-critical-500`} />
          <Typography variant="body2" css={tw`text-critical-500`}>
            {error}
          </Typography>
        </div>
      )}
    </Fragment>
  )
}

const styles = {
  container: (isDragOver?: boolean, error?: string | null) => [
    isDragOver
      ? css`
          background-color: var(--mui-shades-50);
          border-color: var(--mui-primary-500);
        `
      : css`
          background-color: var(--mui-shades-0);
        `,
    error &&
      css`
        border-color: var(--mui-critical-500);
        border-style: solid;
      `,
  ],
  input: [
    css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      z-index: 1;
      appearance: none;
      -webkit-appearance: none;
      opacity: 0;
    `,
  ],
}

export const FileUploadInput = memo(FileUploadInputComponent)
